import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../context/ModalContext';
import { adminService } from '../services/adminService';
import api from '../services/api';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  const tz = 'Asia/Hong_Kong';
  const hkNow = new Date(new Date().toLocaleString('en-US', { timeZone: tz }));
  const hkDate = new Date(date.toLocaleString('en-US', { timeZone: tz }));
  const diff = hkNow - hkDate;
  const oneDay = 86400000;
  const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz });

  if (diff < oneDay && hkDate.getDate() === hkNow.getDate()) {
    return `Today, ${timeStr}`;
  }
  const yesterday = new Date(hkNow);
  yesterday.setDate(yesterday.getDate() - 1);
  if (hkDate.getDate() === yesterday.getDate() && hkDate.getMonth() === yesterday.getMonth()) {
    return `Yesterday, ${timeStr}`;
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz }) +
    ', ' + timeStr;
}

const styles = {
  page: {
    padding: '0',
  },
  card: {
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
    padding: '24px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '0',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  filterRow: {
    display: 'flex',
    gap: '10px',
  },
  dateInput: {
    padding: '8px 12px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#d9e0ea',
    borderRadius: '6px',
    fontSize: '14px',
    width: '150px',
    outline: 'none',
    color: '#1f2937',
  },
  searchInput: {
    padding: '8px 14px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#d9e0ea',
    borderRadius: '6px',
    fontSize: '14px',
    width: '200px',
    outline: 'none',
  },
  divider: {
    borderBottom: '1px solid #d9e0ea',
    marginBottom: '20px',
    marginTop: '10px',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '14px 16px',
    borderBottom: '1px solid #d9e0ea',
    color: '#475569',
    fontWeight: 600,
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '14px 16px',
    borderBottom: '1px solid #eef2f7',
    color: '#1f2937',
    fontSize: '14px',
  },
  tdSubtle: {
    color: '#6b7280',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  badgeDraft: {
    display: 'inline-block',
    padding: '3px 10px',
    fontSize: '11px',
    fontWeight: 600,
    borderRadius: '12px',
    color: '#856404',
    background: '#fff3cd',
  },
  badgeSubmitted: {
    display: 'inline-block',
    padding: '3px 10px',
    fontSize: '11px',
    fontWeight: 600,
    borderRadius: '12px',
    color: '#0f5132',
    background: '#d1e7dd',
  },

  viewBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--company-primary-contrast, #fff)',
    background: 'var(--company-primary-color, #273947)',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  downloadBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--company-primary-color, #273947)',
    background: '#fff',
    border: '1px solid var(--company-primary-color, #273947)',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  deleteBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#b42318',
    background: '#fff',
    border: '1px solid #b42318',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: '15px',
  },
  loadingState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#6b7280',
    fontSize: '15px',
  },
  /* Preview modal */
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#fff',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '900px',
    height: '85vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #e5e7eb',
  },
  modalTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#6b7280',
    padding: '0 4px',
    lineHeight: 1,
  },
  previewFrame: {
    width: '100%',
    flex: 1,
    border: 'none',
  },
};

export default function ClinicSubmissionsPage() {
  const navigate = useNavigate();
  const { showAlert, showConfirm } = useModal();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFilename, setPreviewFilename] = useState('');

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getAllSubmissions();
      setSubmissions(Array.isArray(data?.submissions) ? data.submissions : []);
    } catch (error) {
      console.error('Failed to load submissions:', error);
      await showAlert('Failed to load submissions', 'Error');
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const filteredSubmissions = useMemo(() => {
    return submissions.filter((item) => {
      // Search filter
      const term = searchTerm.trim().toLowerCase();
      const matchesSearch = !term || [
        item.referenceId,
        item.id,
        item.filename,
        item.formName,
      ].some(value => (value || '').toLowerCase().includes(term));

      // Date filter
      let matchesDate = true;
      if (dateFilter) {
        const itemDate = new Date(item.uploadedAt || item.createdAt);
        const filterDate = new Date(dateFilter);
        matchesDate = itemDate.getFullYear() === filterDate.getFullYear() &&
          itemDate.getMonth() === filterDate.getMonth() &&
          itemDate.getDate() === filterDate.getDate();
      }

      return matchesSearch && matchesDate;
    });
  }, [submissions, searchTerm, dateFilter]);

  // Drafts are reopened through their originating link token, which carries
  // the answers saved so far. internal=1 keeps the staff-facing behaviour.
  // Navigates in the SAME tab: opening a new tab let the same form be opened
  // several times at once, which produced duplicate submissions.
  const handleEditDraft = (submission) => {
    if (!submission?.draftToken) return;
    sessionStorage.setItem('formReturnPath', window.location.pathname);
    navigate(`/fill/${submission.draftToken}?verified=1&internal=1`);
  };

  const handlePreview = async (formId, filename) => {
    // iOS Safari (iPad/iPhone) only renders the FIRST page of a PDF shown in an
    // <iframe> and cannot scroll to later pages, so on touch devices we open the
    // PDF in a new tab (native viewer pages correctly). The tab must be opened
    // synchronously inside this click handler — opening it after the await would
    // be treated as an unrequested pop-up and blocked. We open a blank tab now
    // and point it at the PDF once the blob has loaded.
    const isTouchDevice = (('ontouchstart' in window) || navigator.maxTouchPoints > 0);
    let pdfTab = null;
    if (isTouchDevice) {
      // Must NOT pass 'noopener' here: with noopener, window.open always returns
      // null even when the tab opens fine, which made the code think it was
      // blocked and show the pop-up warning every time. Open with a handle, then
      // sever the opener manually for safety.
      pdfTab = window.open('', '_blank');
      if (pdfTab) pdfTab.opener = null;
    }

    try {
      const response = await api.get(`/forms/${formId}/submissions/${encodeURIComponent(filename)}`, {
        params: { preview: true },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });

      if (isTouchDevice) {
        const url = URL.createObjectURL(blob);
        if (pdfTab) {
          pdfTab.location.href = url;
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        } else {
          // The pre-opened tab was blocked before we could use it.
          URL.revokeObjectURL(url);
          await showAlert('Please allow pop-ups to view the PDF.', 'View PDF');
        }
        return;
      }

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const nextUrl = URL.createObjectURL(blob);
      setPreviewUrl(nextUrl);
      setPreviewFilename(filename);
    } catch (error) {
      console.error('Preview failed:', error);
      // Close the blank tab we opened up-front so it doesn't linger.
      if (pdfTab) pdfTab.close();
      await showAlert('Failed to preview PDF', 'Error');
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewFilename('');
  };

  const handleDownload = async (formId, filename) => {
    try {
      const response = await api.get(`/forms/${formId}/submissions/${encodeURIComponent(filename)}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      await showAlert('Failed to download PDF', 'Error');
    }
  };

  const handleDelete = async (submission) => {
    const isDraft = submission.status === 'Drafted';
    const label = submission.filename || submission.patientName || submission.formName || 'this record';
    const confirmed = await showConfirm(
      `Are you sure to permanently delete ${label}? You will not be able to recover this submission.`,
      { title: isDraft ? 'Delete Draft' : 'Delete Submission', okText: 'Delete', danger: true }
    );
    if (!confirmed) return;

    try {
      if (isDraft) {
        await adminService.deleteDraftSubmission(submission.draftToken);
      } else {
        await adminService.deleteSubmission(submission.formId, submission.filename);
      }
      await loadSubmissions();
    } catch (error) {
      console.error('Delete failed:', error);
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to delete record';
      await showAlert(message, 'Error');
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.sectionHeader}>
          <div style={styles.filterRow}>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={styles.dateInput}
            />
            <input
              type="text"
              placeholder="Search template or file name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
          </div>
        </div>

        <div style={styles.divider} />

        {loading && <div style={styles.loadingState}>Loading submissions...</div>}

        {!loading && filteredSubmissions.length === 0 && (
          <div style={styles.emptyState}>No submissions found.</div>
        )}

        {!loading && filteredSubmissions.length > 0 && (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>TEMPLATE NAME</th>
                  <th style={styles.th}>IDENTIFIER</th>
                  <th style={styles.th}>FILE NAME</th>
                  <th style={styles.th}>DATE</th>
                  <th style={styles.th}>STATUS</th>
                  <th style={styles.th}>CHANNEL</th>
                  <th style={styles.th}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubmissions.map((submission, index) => {
                  const isDraft = submission.status === 'Drafted';
                  return (
                  <tr key={`${submission.formId}-${submission.filename || submission.draftToken}-${index}`}>
                    <td style={styles.td}>
                      {submission.formName || 'Unknown'}
                    </td>
                    <td style={styles.td}>
                      {submission.patientName || '—'}
                    </td>
                    <td style={styles.td}>
                      {submission.filename || '—'}
                    </td>
                    <td style={{ ...styles.td, ...styles.tdSubtle }}>
                      {formatDate(submission.uploadedAt || submission.createdAt)}
                    </td>
                    <td style={styles.td}>
                      <span style={isDraft ? styles.badgeDraft : styles.badgeSubmitted}>
                        {isDraft ? 'Drafted' : 'Submitted'}
                      </span>
                    </td>
                    <td style={{ ...styles.td, ...styles.tdSubtle }}>
                      {submission.submissionSource || '—'}
                    </td>

                    <td style={styles.td}>
                      <div style={styles.actions}>
                        {isDraft ? (
                          <button
                            type="button"
                            style={styles.viewBtn}
                            onClick={() => handleEditDraft(submission)}
                            title="Continue filling this draft"
                          >
                            Edit
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              style={styles.viewBtn}
                              onClick={() => handlePreview(submission.formId, submission.filename)}
                            >
                              View PDF
                            </button>
                            <button
                              type="button"
                              style={styles.downloadBtn}
                              onClick={() => handleDownload(submission.formId, submission.filename)}
                            >
                              Download
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          style={styles.deleteBtn}
                          onClick={() => handleDelete(submission)}
                          title="Delete this record"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>

          </div>
        )}
      </div>

      {previewUrl && (
        <div style={styles.modalOverlay} onClick={closePreview}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>{previewFilename}</h3>
              <button type="button" style={styles.closeButton} onClick={closePreview}>
                ×
              </button>
            </div>
            <iframe title={previewFilename} src={previewUrl} style={styles.previewFrame} />
          </div>
        </div>
      )}
    </div>
  );
}
